import { format } from "date-fns";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function humanize(str: string): string {
  if (!str) return "";
  // Replace underscores/dashes with spaces, insert spaces before capitals, and lowercase all
  const result = str
    .replace(/([a-z\d])([A-Z])/g, "$1 $2") // camelCase to camel Case
    .replace(/[_-]+/g, " ") // snake_case or kebab-case to spaces
    .toLowerCase();
  // Capitalize the first letter
  return result.charAt(0).toUpperCase() + result.slice(1);
}

/**
 * A date-only value ("2026-10-01"), formatted without moving it.
 *
 * `new Date("2026-10-01")` is parsed as midnight UTC, so anywhere west of
 * Greenwich it renders as 30 September — a shipment window that opens in
 * October reads as September, which on a trading record is simply wrong.
 * Splitting the string keeps the date the server sent.
 */
/** A timestamp as "3 Sep 2026": the one short date format the app uses. */
export function shortDate(value: string | Date): string {
  return format(new Date(value), "d MMM yyyy");
}

/**
 * A date-only string in a fixed pattern — "12 Apr 2026" by default —
 * parsed the way formatDateOnly parses, so the day never slips a zone.
 */
export function shortDateOnly(value: string | null | undefined, pattern = "d MMM yyyy"): string | null {
  if (!value) return null;
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  return format(new Date(year, month - 1, day), pattern);
}

export function formatDateOnly(
  value: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { month: "short", year: "numeric" },
): string | null {
  if (!value) return null;
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, options);
}

export const OPPORTUNITY_STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Stale", value: "stale" },
  { label: "Closed", value: "closed" },
];

export const DEAL_STATUS_OPTIONS = [
  { label: "Lead", value: "lead" },
  { label: "Qualified", value: "qualified" },
  { label: "In Progress", value: "in_progress" },
  { label: "Won", value: "won" },
  { label: "Lost", value: "lost" },
];
